import { useState } from 'react';

import {
  Box,
  type FormErrorMessageProps,
  type FormLabelProps,
  Input,
  Tag,
  TagCloseButton,
  TagLabel,
} from '@chakra-ui/react';
import { Controller, type FieldPath, type FieldValues } from 'react-hook-form';

import { FormFieldError } from '@/components/Form/FormFieldError';
import { FormFieldHelper } from '@/components/Form/FormFieldHelper';
import { FormFieldItem } from '@/components/Form/FormFieldItem';
import { FormFieldLabel } from '@/components/Form/FormFieldLabel';

import { type FieldCommonProps } from '../FormField.types';

type CommonInputProps = {
  maxLength?: number;
};

export type FieldCreatableInputProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  type: 'creatable-input';
  placeholder?: string;
  label?: React.ReactNode;
  labelProps?: FormLabelProps;
  helper?: React.ReactNode;
  formFieldErrorProps?: FormErrorMessageProps;
} & FieldCommonProps<TFieldValues, TName> &
  CommonInputProps;

function CreatableInputComponent({
  value = [],
  onChange,
  maxLength,
  placeholder,
}: {
  value: Array<string>;
  onChange: (tags: Array<string>) => void;
  maxLength?: number;
  placeholder?: string;
}) {
  const [inputValue, setInputValue] = useState('');

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && inputValue.trim() !== '') {
      onChange([...value, inputValue.trim()]);
      setInputValue('');
      event.preventDefault();
    }
  };

  const handleRemoveTag = (index: number) => {
    const updatedTags = value.filter((_, i) => i !== index);
    onChange(updatedTags);
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      flexWrap="wrap"
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="8px"
      paddingX="12px"
      paddingY="5px"
      columnGap="5px"
    >
      {value.map((tag, index) => (
        <Tag
          m={0.5}
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          borderRadius="md"
          bgColor="white"
          borderColor="gray.100"
          borderWidth={1}
        >
          <TagLabel>{tag}</TagLabel>
          <TagCloseButton
            color="gray.600"
            onClick={() => handleRemoveTag(index)}
          />
        </Tag>
      ))}
      <Input
        disabled={maxLength ? value.length >= maxLength : false}
        placeholder={placeholder}
        borderRadius={8}
        variant="unstyled"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        width="auto"
        flex="1"
        height={8}
      />
    </Box>
  );
}

export function CreatableInput<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  label,
  helper,
  control,
  maxLength,
  labelProps,
  placeholder,
  formFieldErrorProps,
}: FieldCreatableInputProps<TFieldValues, TName>) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <FormFieldItem>
          {label && <FormFieldLabel {...labelProps}>{label}</FormFieldLabel>}
          <CreatableInputComponent
            value={field.value || []}
            onChange={field.onChange}
            maxLength={maxLength}
            placeholder={placeholder}
          />
          {helper && <FormFieldHelper>{helper}</FormFieldHelper>}
          {fieldState.error && (
            <FormFieldError {...formFieldErrorProps}>
              {fieldState.error.message}
            </FormFieldError>
          )}
        </FormFieldItem>
      )}
    />
  );
}
